import {
  BudgetRecommendation,
  GeneralRecommendationResponse,
} from 'api-clients/monolith';
import { createContext, PropsWithChildren, useContext, useState } from 'react';

import { RecommendationPostingDialog } from '@/components/pages/recommendation-posting-dialog';

import type {
  HiringGoalDetails,
  OpeningDetails,
} from '@/components/pages/recommendation-posting-dialog/types';

type OpenRecommendationPostingDialogParams = {
  type: 'general' | 'budget';
  opening: OpeningDetails;
  hiringGoal?: HiringGoalDetails;
  recommendedChannels?: GeneralRecommendationResponse['recommended_channels'];
  budgetRecommendation?: BudgetRecommendation;
};

interface RecommendationContextType {
  isDialogOpen: boolean;
  openRecommendationPostingDialog: (
    params: OpenRecommendationPostingDialogParams,
  ) => void;
  closeRecommendationPostingDialog: () => void;
  recommendationType: 'general' | 'budget';
  opening?: OpeningDetails;
  hiringGoal?: HiringGoalDetails;
  recommendedChannels?: GeneralRecommendationResponse['recommended_channels'];
  budgetRecommendation?: BudgetRecommendation;
  isSubmitting: boolean;
  setIsSubmitting: (isSubmitting: boolean) => void;
  selectedChannels: string[];
  setSelectedChannels: React.Dispatch<React.SetStateAction<string[]>>;
}

/* Context to store the general recommendation state */
const RecommendationContext = createContext<
  RecommendationContextType | undefined
>(undefined);

/* Provider to wrap the app with the general recommendation context */
export function RecommendationProvider({ children }: PropsWithChildren) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [recommendationType, setRecommendationType] = useState<
    'general' | 'budget'
  >('general');
  const [opening, setOpening] = useState<OpeningDetails>();
  const [hiringGoal, setHiringGoal] = useState<HiringGoalDetails>();
  const [recommendedChannels, setRecommendedChannels] =
    useState<GeneralRecommendationResponse['recommended_channels']>();
  const [budgetRecommendation, setBudgetRecommendation] =
    useState<BudgetRecommendation>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedChannels, setSelectedChannels] = useState<string[]>([]);

  /* Open the recommendation posting dialog - to be called by the component that needs to open the dialog */
  const openRecommendationPostingDialog = ({
    type,
    opening,
    hiringGoal,
    recommendedChannels,
    budgetRecommendation,
  }: OpenRecommendationPostingDialogParams) => {
    setRecommendationType(type);
    setOpening(opening);
    setHiringGoal(hiringGoal);
    setRecommendedChannels(recommendedChannels);
    setBudgetRecommendation(budgetRecommendation);
    setSelectedChannels(
      recommendedChannels?.map(channel => channel.slug) ?? [],
    );
    setIsDialogOpen(true);
  };

  /* Close the recommendation posting dialog */
  const closeRecommendationPostingDialog = () => {
    setIsDialogOpen(false);
  };

  return (
    <RecommendationContext.Provider
      value={{
        isDialogOpen,
        openRecommendationPostingDialog,
        closeRecommendationPostingDialog,
        recommendationType,
        budgetRecommendation,
        opening,
        hiringGoal,
        recommendedChannels,
        isSubmitting,
        setIsSubmitting,
        selectedChannels,
        setSelectedChannels,
      }}
    >
      {children}
      <RecommendationPostingDialog
        isOpen={isDialogOpen}
        setIsOpen={setIsDialogOpen}
      />
    </RecommendationContext.Provider>
  );
}

/* Hook to use the general recommendation context */
export function useRecommendationContext() {
  const context = useContext(RecommendationContext);

  if (!context) {
    throw new Error(
      'useRecommendationContext must be used within a RecommendationProvider',
    );
  }

  return context;
}
